import { useEffect, useState, type ReactElement } from 'react';
import { taxApi, type BwaResult, type EuerResult, type UstvaResult, type UstJahrResult, type EstResult, type GewStResult, ApiError, yearUnlockApi, type JahrStatus } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { Button, Card, EmptyState, ErrorNote, PageHeader, euro } from '../ui';
import { ElsterPanel } from './elster/SubmitFlow';
import { SubmissionHistory } from './elster/SubmissionHistory';
import { TaxOverview } from './TaxOverview';
import { UstvaPeriodPicker } from './UstvaPeriodPicker';
import { EstWizard } from './wizard/EstWizard';

type Tab = 'overview' | 'ustva' | 'ustjahr' | 'est' | 'gewst' | 'euer' | 'bwa';

const TABS: [Tab, string][] = [
  ['overview', 'Übersicht'],
  ['ustva', 'USt-Voranmeldung'],
  ['ustjahr', 'Umsatzsteuer (Jahr)'],
  ['est', 'Einkommensteuer'],
  ['gewst', 'Gewerbesteuer'],
  ['euer', 'EÜR'],
  ['bwa', 'BWA'],
];

export default function Taxes(): ReactElement {
  const [tab, setTab] = useState<Tab>('overview');
  const { active } = useCompany();

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Steuern & Auswertungen" subtitle="Umsatz-, Einkommen- und Gewerbesteuer, EÜR und BWA aus deinen echten Daten." />
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${tab === k ? 'border-blue-600 text-blue-700 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            {l}
          </button>
        ))}
      </div>
      {active && tab === 'overview' && <TaxOverview companyId={active.id} />}
      {active && tab === 'ustva' && <Ustva companyId={active.id} />}
      {active && tab === 'ustjahr' && <UstJahr companyId={active.id} />}
      {active && tab === 'est' && <Est companyId={active.id} />}
      {active && tab === 'gewst' && <Gewst companyId={active.id} />}
      {active && tab === 'euer' && <Euer companyId={active.id} />}
      {active && tab === 'bwa' && <Bwa companyId={active.id} />}
    </div>
  );
}

function useLocked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 402;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function Ustva({ companyId }: { companyId: string }): ReactElement {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [res, setRes] = useState<UstvaResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function calc() {
    setError(null); setBusy(true);
    try { setRes(await taxApi.ustva(companyId, period)); } catch (e) { setError(e); setRes(null); } finally { setBusy(false); }
  }

  if (useLocked(error)) return <Upgrade text="Umsatzsteuer-Voranmeldung ab Professional." />;

  return (
    <Card className="space-y-4">
      {error ? <ErrorNote error={error} /> : null}
      <UstvaPeriodPicker companyId={companyId} period={period} setPeriod={(p) => { setPeriod(p); setRes(null); }} />
      <Button onClick={calc} busy={busy}>Berechnen</Button>
      {res && (
        <>
          {res.zusammengefasst && res.zusammengefasst.length > 0 && (
            <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
              <p className="text-xs text-blue-900 dark:text-blue-200">
                <strong>Alle Betriebe in einer Meldung.</strong> Umsatzsteuerlich hast du nur ein
                Unternehmen (§ 2 Abs. 1 UStG) – die Umsätze von{' '}
                {res.zusammengefasst.join(', ')} sind hier enthalten.
              </p>
            </div>
          )}
        <div className="space-y-2 border-t border-gray-100 pt-4 text-sm">
          <Row label="Umsatz (netto)" value={euro(res.umsatzNetCents)} />
          <Row label="Umsatzsteuer (vereinnahmt)" value={euro(res.umsatzsteuerCents)} />
          <Row label="Vorsteuer (gezahlt)" value={euro(res.vorsteuerCents)} />
          <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold">
            <span>{res.zahllastCents >= 0 ? 'Zahllast ans Finanzamt' : 'Erstattung'}</span>
            <span>{euro(Math.abs(res.zahllastCents))}</span>
          </div>
          <ElsterPanel
            returnType="ustva"
            period={period}
            companyId={companyId}
            historyType="ustva"
            data={{
              umsatz19NetCents: res.umsatzNetCents,
              umsatzsteuerCents: res.umsatzsteuerCents,
              vorsteuerCents: res.vorsteuerCents,
            }}
          />
        </div>
        </>
      )}
    </Card>
  );
}

function UstJahr({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [res, setRes] = useState<UstJahrResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function calc() {
    setError(null); setBusy(true);
    try { setRes(await taxApi.ustJahr(companyId, year)); } catch (e) { setError(e); setRes(null); } finally { setBusy(false); }
  }
  if (useLocked(error)) return <Upgrade text="Umsatzsteuer-Jahreswerte ab Professional." />;

  return (
    <Card className="space-y-4">
      {error ? <ErrorNote error={error} /> : null}
      <YearPicker companyId={companyId} year={year} setYear={setYear} onGo={calc} busy={busy} />
      {res && (
        <div className="space-y-2 border-t border-gray-100 pt-4 text-sm dark:border-gray-700">
          <Row label="Umsatz (netto)" value={euro(res.umsatzNetCents)} />
          <Row label="Umsatzsteuer (vereinnahmt)" value={euro(res.umsatzsteuerCents)} />
          <Row label="Vorsteuer (gezahlt)" value={euro(res.vorsteuerCents)} />
          {res.jahresZahllastCents !== undefined && (
            <Row
              label={(res.jahresZahllastCents ?? 0) >= 0 ? 'USt-Schuld (Jahr)' : 'USt-Guthaben (Jahr)'}
              value={euro(Math.abs(res.jahresZahllastCents ?? 0))}
            />
          )}
          {res.vorauszahlungenCents !== undefined && (
            <Row
              label="abzgl. bereits geleistete Voranmeldungen (UStVA)"
              value={`− ${euro(res.vorauszahlungenCents ?? 0)}`}
            />
          )}
          <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold dark:border-gray-700">
            <span>{res.zahllastCents >= 0 ? 'Abschlusszahlung (Jahr)' : 'Erstattung (Jahr)'}</span>
            <span>{euro(Math.abs(res.zahllastCents))}</span>
          </div>
          <p className="pt-1 text-xs text-gray-400">
            Die Jahreserklärung verrechnet die unterjährig per UStVA geleisteten Vorauszahlungen.
            Berücksichtigt werden nur als eingereicht markierte Voranmeldungen.
          </p>
          <ElsterPanel returnType="ustJahr" period={String(year)} companyId={companyId} historyType="ustJahr" />
        </div>
      )}
    </Card>
  );
}

function Est({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [res, setRes] = useState<EstResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  /** Gespeicherter Entwurf fuer das gewaehlte Jahr (Anzahl erfasster Felder). */
  const [draftFields, setDraftFields] = useState<number | null>(null);

  // Entwurfsstatus laden, damit ein begonnener Assistent sichtbar ist statt
  // hinter "starten" zu verschwinden.
  useEffect(() => {
    let alive = true;
    taxApi.listReturns(companyId)
      .then((list) => {
        if (!alive) return;
        const e = list.find((x) => x.type === 'est' && x.period === String(year));
        const answers = (e?.data as { answers?: Record<string, unknown> } | undefined)?.answers;
        setDraftFields(answers ? Object.keys(answers).length : null);
      })
      .catch(() => setDraftFields(null));
    return () => { alive = false; };
  }, [companyId, year, showWizard]);

  async function calc() {
    setError(null); setBusy(true);
    try { setRes(await taxApi.est(companyId, year)); } catch (e) { setError(e); setRes(null); } finally { setBusy(false); }
  }
  if (useLocked(error)) return <Upgrade text="Einkommensteuer-Schätzung ab Professional." />;

  if (showWizard) {
    return <EstWizard companyId={companyId} year={year} onClose={() => setShowWizard(false)} />;
  }

  return (
    <Card className="space-y-4">
      {error ? <ErrorNote error={error} /> : null}
      <YearPicker companyId={companyId} year={year} setYear={setYear} onGo={calc} busy={busy} />

      {/* Prominent wizard entry point */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/20">
        <p className="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-200">
          Steuererklärung mit Assistent erstellen
        </p>
        <p className="mb-3 text-xs text-blue-700 dark:text-blue-300">
          Schritt für Schritt durch alle Anlagen – wie WISO Steuer, direkt in Saldify. Angaben werden aus deiner Buchhaltung vorausgefüllt.
        </p>
        {draftFields != null && draftFields > 0 ? (
          <>
            <p className="mb-3 rounded-lg bg-white/60 px-3 py-2 text-xs text-blue-900 dark:bg-blue-900/30 dark:text-blue-200">
              Für {year} liegt ein gespeicherter Entwurf mit {draftFields}{' '}
              {draftFields === 1 ? 'Angabe' : 'Angaben'} vor.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setShowWizard(true)}>Steuererklärung fortsetzen</Button>
            </div>
          </>
        ) : (
          <Button onClick={() => setShowWizard(true)}>Steuererklärungsassistent starten</Button>
        )}
      </div>

      {res && (
        <div className="space-y-2 border-t border-gray-100 pt-4 text-sm dark:border-gray-700">
          <Row label="Gewinn (Einnahmen − Ausgaben, netto)" value={euro(res.gewinnCents)} />
          <Row label="zu versteuerndes Einkommen (vereinfacht)" value={euro(res.zveCents)} />
          <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold dark:border-gray-700">
            <span>Einkommensteuer (geschätzt)</span>
            <span>{euro(res.estCents)}</span>
          </div>
          <Row label="Durchschnittssteuersatz" value={`${res.durchschnittssatzProzent} %`} />
          <Disclaimer text="Grobe Schätzung nach Grundtarif (§32a EStG), nur auf Basis deines Gewinns – ohne weitere Einkünfte, Ehegatten-Splitting, Sonderausgaben, Vorsorge oder Soli/Kirchensteuer. Maßgeblich ist deine Steuererklärung." />
        </div>
      )}
      <SubmissionHistory companyId={companyId} type="est" refreshKey={0} />
    </Card>
  );
}

function Gewst({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [res, setRes] = useState<GewStResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // Manuelle Korrektur des Hebesatzes.
  const [editing, setEditing] = useState(false);
  const [manualHebesatz, setManualHebesatz] = useState(400);
  const [savingHb, setSavingHb] = useState(false);

  async function calc() {
    setError(null); setBusy(true); setEditing(false);
    try { setRes(await taxApi.gewst(companyId, year)); } catch (e) { setError(e); setRes(null); } finally { setBusy(false); }
  }
  async function saveHebesatz() {
    setSavingHb(true);
    try {
      await taxApi.setHebesatz(companyId, manualHebesatz);
      setEditing(false);
      setRes(await taxApi.gewst(companyId, year)); // mit korrigiertem (jetzt gecachtem) Wert neu rechnen
    } catch (e) { setError(e); } finally { setSavingHb(false); }
  }
  if (useLocked(error)) return <Upgrade text="Gewerbesteuer-Schätzung ab Professional." />;

  const quelleLabel: Record<GewStResult['hebesatzQuelle'], string> = {
    gemeinde: 'automatisch ermittelt',
    manuell: 'manuell korrigiert',
    standard: 'Standardwert (Ort unbekannt)',
  };

  return (
    <Card className="space-y-4">
      {error ? <ErrorNote error={error} /> : null}
      <YearPicker companyId={companyId} year={year} setYear={setYear} onGo={calc} busy={busy} />
      <p className="text-xs text-gray-400">Der Hebesatz wird automatisch aus dem Firmen-Ort ermittelt (einmalig recherchiert und gespeichert). Du kannst ihn bei Bedarf korrigieren.</p>
      {res && (
        <div className="space-y-2 border-t border-gray-100 pt-4 text-sm dark:border-gray-700">
          {!res.pflichtig && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              Diese Firma ist als <strong>nicht gewerbesteuerpflichtig</strong> markiert (Einstellungen → Firma). Die Schätzung ist daher nur informativ.
            </p>
          )}

          {/* Hebesatz-Zeile mit Quelle + Korrektur */}
          <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-900">
            {editing ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Hebesatz:</span>
                <input type="number" value={manualHebesatz} min={200} max={900}
                  onChange={(e) => setManualHebesatz(Number(e.target.value))}
                  className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                <span className="text-sm text-gray-500">%</span>
                <Button onClick={saveHebesatz} busy={savingHb}>Speichern</Button>
                <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:underline">abbrechen</button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Hebesatz <strong>{res.hebesatzProzent} %</strong>
                  {res.hebesatzCity ? ` (${res.hebesatzCity})` : ''}
                  <span className="ml-1 text-xs text-gray-400">– {quelleLabel[res.hebesatzQuelle]}</span>
                </span>
                <button
                  onClick={() => { setManualHebesatz(res.hebesatzProzent); setEditing(true); }}
                  className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  korrigieren
                </button>
              </div>
            )}
          </div>

          <Row label="Gewinn (netto)" value={euro(res.gewinnCents)} />
          <Row label="Gewerbeertrag (abgerundet)" value={euro(res.gewerbeertragCents)} />
          {res.freibetragAngewendet && <Row label="− Freibetrag (24.500 €)" value={euro(res.freibetragCents)} />}
          <Row label="Steuermessbetrag (3,5 %)" value={euro(res.messbetragCents)} />
          <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold dark:border-gray-700">
            <span>Gewerbesteuer (geschätzt)</span>
            <span>{euro(res.steuerCents)}</span>
          </div>
          <Disclaimer text="Grobe Schätzung: Gewerbeertrag × 3,5 % × Hebesatz. Hinzu-/Kürzungen (§8/§9 GewStG) und die Anrechnung auf die Einkommensteuer (§35 EStG) sind nicht berücksichtigt. Der automatisch ermittelte Hebesatz ist ein Richtwert – im Zweifel mit dem Gewerbesteuerbescheid abgleichen." />
          <ElsterPanel returnType="gewst" period={String(year)} companyId={companyId} historyType="gewst" />
        </div>
      )}
    </Card>
  );
}

function Euer({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [res, setRes] = useState<EuerResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [pdfBusy, setPdfBusy] = useState(false);
  async function calc() {
    setError(null); setBusy(true);
    try { setRes(await taxApi.euer(companyId, year)); } catch (e) { setError(e); setRes(null); } finally { setBusy(false); }
  }
  async function pdf() {
    setPdfBusy(true);
    try { downloadBlob(await taxApi.euerPdf(companyId, year), `EUER-${year}.pdf`); } catch (e) { setError(e); } finally { setPdfBusy(false); }
  }
  if (useLocked(error)) return <Upgrade text="EÜR ab Plus." />;

  return (
    <Card className="space-y-4">
      {error ? <ErrorNote error={error} /> : null}
      <YearPicker companyId={companyId} year={year} setYear={setYear} onGo={calc} busy={busy} />
      {res && (
        <div className="space-y-2 border-t border-gray-100 pt-4 text-sm dark:border-gray-700">
          <Row label="Einnahmen" value={euro(res.einnahmenCents)} />
          <Row label="Ausgaben" value={euro(res.ausgabenCents)} />
          <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold dark:border-gray-700">
            <span>Überschuss</span><span>{euro(res.ueberschussCents)}</span>
          </div>
          {Object.keys(res.ausgabenNachKategorie).length > 0 && (
            <div className="pt-2">
              <div className="mb-1 text-xs font-medium text-gray-500">Betriebsausgaben (Anlage-EÜR-Gliederung)</div>
              {Object.entries(res.ausgabenNachKategorie).map(([k, v]) => <Row key={k} label={k} value={euro(v)} />)}
            </div>
          )}
          <div className="pt-3">
            <Button variant="secondary" onClick={pdf} busy={pdfBusy}>📄 Als PDF exportieren</Button>
            <p className="mt-1 text-xs text-gray-400">Anlage-EÜR-Gliederung inkl. kontengenauem Nachweis – für Bank, Steuerberater oder eigene Unterlagen.</p>
          </div>
          <ElsterPanel returnType="euer" period={String(year)} companyId={companyId} historyType="euer" />
        </div>
      )}
    </Card>
  );
}

function Bwa({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [res, setRes] = useState<BwaResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [pdfBusy, setPdfBusy] = useState(false);
  async function calc() {
    setError(null); setBusy(true);
    try { setRes(await taxApi.bwa(companyId, year)); } catch (e) { setError(e); setRes(null); } finally { setBusy(false); }
  }
  async function pdf() {
    setPdfBusy(true);
    try { downloadBlob(await taxApi.bwaPdf(companyId, year), `BWA-${year}.pdf`); } catch (e) { setError(e); } finally { setPdfBusy(false); }
  }
  if (useLocked(error)) return <Upgrade text="BWA ab Professional." />;

  return (
    <Card className="space-y-4">
      {error ? <ErrorNote error={error} /> : null}
      <YearPicker companyId={companyId} year={year} setYear={setYear} onGo={calc} busy={busy} />
      {res && (
        <div className="space-y-2 border-t border-gray-100 pt-4 text-sm dark:border-gray-700">
          <Row label="Umsatzerlöse" value={euro(res.erloeseCents)} />
          <Row label="Kosten (inkl. Wareneinsatz)" value={euro(res.kostenCents)} />
          <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold dark:border-gray-700">
            <span>Betriebsergebnis</span><span>{euro(res.ergebnisCents)}</span>
          </div>
          <Row label="Umsatzrentabilität" value={`${res.margeProzent} %`} />
          <div className="pt-3">
            <Button variant="secondary" onClick={pdf} busy={pdfBusy}>📄 Als PDF exportieren</Button>
            <p className="mt-1 text-xs text-gray-400">DATEV-Standard-BWA (Form 01) inkl. kontengenauem Nachweis – für Bank, Steuerberater oder eigene Unterlagen.</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactElement {
  return <div className="flex justify-between text-gray-600 dark:text-gray-300"><span>{label}</span><span>{value}</span></div>;
}
function Disclaimer({ text }: { text: string }): ReactElement {
  return <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400">⚠️ {text}</p>;
}
/**
 * Jahresauswahl mit Hinweis auf gesperrte Jahre.
 *
 * Der Hinweis steht BEI DER AUSWAHL, nicht erst beim Einreichen — sonst
 * erfaehrt der Kunde erst nach getaner Arbeit, dass das Jahr kostet.
 */
function YearPicker({ year, setYear, onGo, busy, companyId }: {
  year: number; setYear: (y: number) => void; onGo: () => void; busy: boolean;
  companyId?: string;
}): ReactElement {
  const [jahre, setJahre] = useState<JahrStatus[] | null>(null);

  useEffect(() => {
    if (!companyId) return;
    yearUnlockApi.list(companyId).then((d) => setJahre(d.jahre)).catch(() => undefined);
  }, [companyId]);

  const status = jahre?.find((j) => j.jahr === year);
  const gesperrt = status?.status === 'gesperrt';

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Jahr</span>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="w-32 rounded-xl border border-gray-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
        </label>
        <Button onClick={onGo} busy={busy}>Berechnen</Button>
      </div>
      {/* Berechnen und Ansehen bleiben frei — nur das Einreichen kostet. */}
      {gesperrt && status && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Auswerten kannst du {year} jederzeit. Für die Übermittlung ans Finanzamt ist eine
          einmalige Freischaltung nötig
          {status.preisCents > 0 ? ` (${(status.preisCents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })})` : ''}
          {' '}– das laufende Jahr und das Vorjahr sind im Tarif enthalten.
        </p>
      )}
    </div>
  );
}
function Upgrade({ text }: { text: string }): ReactElement {
  return <EmptyState title={text} hint="Upgrade deinen Tarif, um diese Auswertung zu nutzen." action={<a href="/app/einstellungen/tarif"><Button>Tarif ansehen</Button></a>} />;
}
